import MenuIcon from '@mui/icons-material/Menu';
//import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import type { Theme } from '@mui/material';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  AppBar as MuiAppBar,
  Stack,
  styled,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { Logo } from '@src/components/logo';
import { useAppContext } from '@src/contexts';
import {
  CalendarMenu,
  ContactsMenu,
  ContactTodayMenu,
  DeliveredResponseTodayMenu,
  ExclusionsMenu,
  ExportMenu,
  ProjectsMenu,
  UsersMenu,
  LogoutMenu,
  StatisticsMenu,
  SwitchProjectMenu,
  useProjectContext,
  UserMenu,
  useSessionContext,
} from '@src/modules';
import { useTheme } from '@src/themes';
import { FC, Fragment, PropsWithChildren, useCallback, useRef, useState } from 'react';

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: 'solid',
  borderColor: (theme.vars ?? theme).palette.divider,
  boxShadow: 'none',
  zIndex: theme.zIndex.drawer + 1,
}));

const getDrawerSxTransitionMixin = (isExpanded: boolean, property: string) => ({
  transition: (theme: Theme) =>
    theme.transitions.create(property, {
      easing: theme.transitions.easing.sharp,
      duration: isExpanded ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
});

const getDrawerWidthTransitionMixin = (isExpanded: boolean) => ({
  ...getDrawerSxTransitionMixin(isExpanded, 'width'),
  overflowX: 'hidden',
});

const NavBar: FC<PropsWithChildren> = ({ children }) => {
  const { COLORS } = useTheme();
  const { isAdmin, isManager, userLogged } = useSessionContext();
  const { isDesktop, isMobile } = useAppContext();
  const { projectLogo, projectName } = useProjectContext();

  const [isDesktopNavigationExpanded, setIsDesktopNavigationExpanded] = useState(true);
  const [isMobileNavigationExpanded, setIsMobileNavigationExpanded] = useState(false);

  const isNavigationExpanded = isDesktop ? isDesktopNavigationExpanded : isMobileNavigationExpanded;

  const setIsNavigationExpanded = useCallback(
    (newExpanded: boolean) => {
      if (isDesktop) {
        setIsDesktopNavigationExpanded(newExpanded);
        return;
      }
      setIsMobileNavigationExpanded(newExpanded);
    },
    [isDesktop]
  );

  const selectedItemIdRef = useRef('');

  const handleSetNavigationExpanded = useCallback(
    (newExpanded: boolean) => () => {
      setIsNavigationExpanded(newExpanded);
    },
    [setIsNavigationExpanded]
  );

  const toggleNavigationExpanded = useCallback(() => {
    setIsNavigationExpanded(!isNavigationExpanded);
  }, [isNavigationExpanded, setIsNavigationExpanded]);

  const handleNavigationLinkClick = useCallback(() => {
    selectedItemIdRef.current = '';
    setIsMobileNavigationExpanded(false);
  }, [setIsMobileNavigationExpanded]);

  const getDrawerSharedSx = useCallback(
    (isMini: boolean, isTemporary: boolean) => {
      const drawerWidth = isMini ? 64 : 264;

      return {
        displayPrint: 'none',
        width: drawerWidth,
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.25)',
        flexShrink: 0,
        ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        ...(isTemporary ? { position: 'absolute' } : {}),
        [`& .MuiDrawer-paper`]: {
          position: 'absolute',
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundImage: 'none',
          ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        },
      };
    },
    [isNavigationExpanded]
  );

  const getMenuIcon = useCallback(
    (isExpanded: boolean) => {
      const expandMenuActionText = 'Expand';
      const collapseMenuActionText = 'Collapse';

      return (
        <Tooltip title={`${isExpanded ? 'Fechar' : 'Abrir'} menu`} enterDelay={1000}>
          <div>
            <IconButton
              size='large'
              aria-controls='menu-appbar'
              aria-haspopup='true'
              aria-label={`${isExpanded ? collapseMenuActionText : expandMenuActionText} navigation menu`}
              onClick={toggleNavigationExpanded}
              color='inherit'
            >
              {isExpanded ? (
                <MenuOpenIcon
                  sx={{
                    fontSize: 32,
                  }}
                />
              ) : (
                <MenuIcon
                  sx={{
                    fontSize: 32,
                  }}
                />
              )}
            </IconButton>
          </div>
        </Tooltip>
      );
    },
    [toggleNavigationExpanded]
  );

  const getDrawerContent = useCallback(
    (isMini: boolean, viewport: 'phone' | 'tablet' | 'desktop') => (
      <Fragment>
        <Toolbar />
        <Box
          component='nav'
          aria-label={`${viewport.charAt(0).toUpperCase()}${viewport.slice(1)}`}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'auto',
          }}
        >
          <Stack direction={'column'}>
            <Divider />
            {isMobile === false && (
              <Fragment>
                <List sx={{ padding: 0, marginY: 1 }}>
                  <ContactTodayMenu variant={'default'} isMini={isMini} />
                  <DeliveredResponseTodayMenu variant={'default'} isMini={isMini} />
                </List>
                <Divider />
              </Fragment>
            )}
            <List sx={{ padding: 0, marginY: 1 }}>
              <CalendarMenu isMini={isMini} />
              <ContactsMenu isMini={isMini} />
              <ExclusionsMenu isMini={isMini} />
              <StatisticsMenu isMini={isMini} />
              {(isAdmin || isManager) && <Divider sx={{ width: '100%' }} />}
              <ExportMenu isMini={isMini} />
              <UsersMenu isMini={isMini} />
              <ProjectsMenu isMini={isMini} />
            </List>
          </Stack>

          <Stack direction={'column'}>
            <Divider />
            <SwitchProjectMenu isMini={isMini} viewport={viewport} />
            <UserMenu isMini={isMini} />
            <List sx={{ padding: 0, marginY: 1 }}>
              <LogoutMenu isMini={isMini} />
            </List>
          </Stack>
        </Box>
      </Fragment>
    ),
    [handleNavigationLinkClick, isAdmin, isManager /* projectStepFeatures */]
  );

  const isDesktopMini = !isDesktopNavigationExpanded;
  const isMobileMini = !isMobileNavigationExpanded;

  if (userLogged === false) return <>{children}</>;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        overflow: 'hidden',
        height: `calc(100vh - ${isMobile ? '31px' : '50px'})`,
        width: '100vw',
      }}
    >
      <AppBar position='fixed'>
        <Toolbar disableGutters>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <Stack direction='row' alignItems='center'>
              <Fragment>
                <Box
                  sx={{
                    mr: { sm: 1 },
                    display: { md: 'none' },
                  }}
                >
                  {getMenuIcon(isMobileNavigationExpanded)}
                </Box>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    mr: 1,
                  }}
                >
                  {getMenuIcon(isDesktopNavigationExpanded)}
                </Box>
              </Fragment>
              {isMobile === true && <Stack direction='row' sx={{ width: '56px', height: '56px' }} />}
            </Stack>

            <Stack direction='row' alignItems='center' gap={1}>
              <Logo width={isMobile ? 56 : 76} opacity={0.5} />
              {isMobile !== true && projectLogo !== null && (
                <Fragment>
                  <AddRoundedIcon
                    sx={{
                      fontSize: 16,
                      color: COLORS.primary.contrast,
                      opacity: 0.5,
                    }}
                  />
                  <img src={`/assets/${projectLogo}`} alt={projectName} width={isMobile ? 36 : 48} />
                </Fragment>
              )}
            </Stack>

            {isMobile === true ? (
              <Stack direction='row' alignItems='center'>
                <DeliveredResponseTodayMenu variant={'mobile'} isMini={isMobileMini} />
                <ContactTodayMenu variant={'mobile'} isMini={isMobileMini} />
              </Stack>
            ) : (
              <Stack direction='row' sx={{ width: '56px', height: '56px' }} />
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Fragment>
        <Drawer
          variant='temporary'
          open={isMobileNavigationExpanded}
          onClose={handleSetNavigationExpanded(false)}
          sx={{
            display: {
              xs: 'block',
              md: 'none',
            },
            ...getDrawerSharedSx(false, true),
          }}
        >
          {getDrawerContent(false, 'phone')}
        </Drawer>
        <Drawer
          variant='permanent'
          sx={{
            display: {
              xs: 'none',
              sm: 'block',
              md: 'none',
            },
            ...getDrawerSharedSx(isMobileMini, false),
          }}
        >
          {getDrawerContent(isMobileMini, 'tablet')}
        </Drawer>
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: 'none', md: 'block' },
            ...getDrawerSharedSx(isDesktopMini, false),
          }}
        >
          {getDrawerContent(isDesktopMini, 'desktop')}
        </Drawer>
      </Fragment>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
        }}
      >
        <Toolbar sx={{ displayPrint: 'none' }} />
        {/*
          Este e o elemento que rola: limitar a largura dele joga a barra de
          rolagem para o meio da tela. A largura do conteudo e responsabilidade
          do Container do AppLayout.
        */}
        <Box
          component='main'
          sx={{
            justifyContent: 'flex-start',
            paddingX: isMobile ? 1 / 2 : 2,
            overflowY: 'auto',
            height: 'calc(100vh - 50px)',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export { NavBar };
